import React, { useEffect, useState, useRef, useCallback } from 'react';
import API from '../api';

const INITIAL_PAGE_SIZE = 16;
const OLDER_PAGE_SIZE = 30;
const POLL_INTERVAL_MS = 6000;       // visible tab
const POLL_BACKOFF_HIDDEN_MS = 30000; // tab hidden — long backoff

async function compressImageToJpeg(file, maxDim = 1920, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
        else { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image decode failed')); };
    img.src = url;
  });
}

function mergeNewMessages(existing, incoming) {
  if (!incoming || incoming.length === 0) return existing;
  const map = new Map(existing.map(m => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return Array.from(map.values()).sort((a, b) => a.id - b.id);
}

function mergeOlderMessages(existing, incoming) {
  if (!incoming || incoming.length === 0) return existing;
  const existingIds = new Set(existing.map(m => m.id));
  const fresh = incoming.filter(m => !existingIds.has(m.id));
  return [...fresh, ...existing];
}

function dataUrlToBlob(dataUrl) {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, comma);
  const b64 = dataUrl.slice(comma + 1);
  const mime = (meta.match(/data:([^;]+)/) || [])[1] || 'application/octet-stream';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Open (view) an attachment immediately — no share prompt. Converts the base64
// data URL to a blob: URL and opens it in a new tab/viewer, which is what iOS,
// Android and desktop all handle natively for PDFs and images.
function openDataUrl(dataUrl) {
  let blob;
  try { blob = dataUrlToBlob(dataUrl); } catch (_) { blob = null; }
  if (!blob) { try { window.open(dataUrl, '_blank', 'noopener'); } catch (_) {} return; }
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (_) {
    try { window.open(blobUrl, '_blank', 'noopener'); } catch (_) {}
  }
  // Keep the URL alive long enough for the viewer tab to load it.
  setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 60000);
}

// Robust cross-platform save. iOS Safari/PWA ignore <a download> on data: URLs
// and choke on window.open of large base64 URLs, so:
//   1. Try the native Share sheet (navigator.share with a File) — this is the
//      reliable iOS path and lets the user Save to Files / iCloud / etc.
//   2. Fall back to an <a download> on a blob: URL (desktop + Android).
//   3. Last resort: open the blob URL in a new tab.
async function downloadDataUrl(dataUrl, filename) {
  const name = filename || 'attachment';
  let blob;
  try { blob = dataUrlToBlob(dataUrl); } catch (_) { blob = null; }

  if (blob) {
    // 1) Native share sheet (best for iOS)
    try {
      const file = new File([blob], name, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
        return;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return; // user dismissed the sheet
      // otherwise fall through to blob-url methods
    }

    // 2) <a download> on a blob URL
    const blobUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // 3) iOS ignores download — also open the blob so the viewer can save it
      const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isIOS) { try { window.open(blobUrl, '_blank'); } catch (_) {} }
    } finally {
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 15000);
    }
    return;
  }

  // Blob conversion failed — last-ditch direct open
  try { window.open(dataUrl, '_blank', 'noopener'); } catch (_) {}
}

export default function ChatWindow({ roomId, studentName }) {
  const [messages, setMessages] = useState([]);
  const [fileData, setFileData] = useState({});   // message.id -> data URL (lazy-loaded, cached)
  const fetchingFilesRef = useRef(new Set());     // ids currently being fetched (or already attempted)
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [replyTo, setReplyTo] = useState(null);       // { id, sender_name, content, file_type, ... }
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [seenByModal, setSeenByModal] = useState(null); // message object when open
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastMarkedRef = useRef(0);
  const myName = localStorage.getItem('name') || '';
  const myRole = localStorage.getItem('role') || '';
  const myUserId = parseInt(localStorage.getItem('user_id') || '0', 10);
  const isAdmin = myRole === 'admin';

  const lastId = messages.length > 0 ? messages[messages.length - 1].id : 0;
  const firstId = messages.length > 0 ? messages[0].id : 0;

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return (el.scrollHeight - el.scrollTop - el.clientHeight) < 120;
  };

  const scrollToBottom = useCallback((smooth = true) => {
    // Two-pronged: direct scrollTop on the container (deterministic) AND a
    // scrollIntoView on the sentinel (covers cases where the container's
    // computed scrollHeight lags the layout).
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
  }, []);

  // Belt-and-braces "stick to bottom on open" — message bubbles with base64
  // images keep growing as their decoded pixels arrive, so a single scroll
  // call right after setMessages typically lands above the bottom and the
  // user sees older messages. We schedule a short chain of follow-up scrolls
  // and an extra one whenever an <img> inside the list fires onLoad.
  const stickToBottom = useCallback(() => {
    const tries = [0, 50, 150, 350, 700, 1200];
    tries.forEach(ms => setTimeout(() => scrollToBottom(false), ms));
  }, [scrollToBottom]);

  // Mark messages up to a given id as read (debounced via lastMarkedRef)
  const markReadUpTo = useCallback(async (upToId) => {
    if (!roomId || !upToId) return;
    if (upToId <= lastMarkedRef.current) return;
    lastMarkedRef.current = upToId;
    try {
      await API.post('/chat/mark-read', { room_id: roomId, up_to_message_id: upToId });
      // Locally clear the "unread" badge state cached elsewhere
      localStorage.setItem(`chat_seen_${roomId}`, String(upToId));
    } catch (_) { /* best effort */ }
  }, [roomId]);

  // Fetch new messages (id > lastId) — polling path
  const fetchNew = useCallback(async () => {
    if (!roomId) return;
    if (document.hidden) return;
    try {
      const sinceId = lastMarkedRef.current === 0 && messages.length > 0
        ? messages[messages.length - 1].id
        : (messages.length > 0 ? messages[messages.length - 1].id : 0);
      const res = await API.get(`/chat/messages/${roomId}?since_id=${sinceId}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        const wasNearBottom = isNearBottom();
        setMessages(prev => mergeNewMessages(prev, res.data));
        if (wasNearBottom) {
          // Defer to next paint so DOM is up to date
          requestAnimationFrame(() => scrollToBottom(true));
        }
        // Mark the just-fetched range as read if tab is visible
        if (!document.hidden) {
          const newest = res.data[res.data.length - 1].id;
          markReadUpTo(newest);
        }
      } else {
        // Even if there are no new messages, refresh read receipts on visible
        // messages periodically so the sender sees ticks turn blue.
        // Cheap: just refetch our latest small window.
        if (messages.length > 0) {
          const lo = messages[Math.max(0, messages.length - 20)].id;
          try {
            const r2 = await API.get(`/chat/messages/${roomId}?since_id=${lo - 1}`);
            if (Array.isArray(r2.data) && r2.data.length > 0) {
              setMessages(prev => mergeNewMessages(prev, r2.data));
            }
          } catch (_) {}
        }
      }
    } catch (_) { /* swallow — retried on next tick */ }
  }, [roomId, messages, markReadUpTo, scrollToBottom]);

  // Initial load — last INITIAL_PAGE_SIZE only
  useEffect(() => {
    if (!roomId) return;
    setMessages([]);
    setFileData({});
    fetchingFilesRef.current = new Set();
    setHasMoreOlder(true);
    setReplyTo(null);
    lastMarkedRef.current = 0;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(`/chat/messages/${roomId}?limit=${INITIAL_PAGE_SIZE}`);
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        setMessages(data);
        if (data.length < INITIAL_PAGE_SIZE) setHasMoreOlder(false);
        stickToBottom();
        if (data.length > 0) {
          markReadUpTo(data[data.length - 1].id);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [roomId, stickToBottom, markReadUpTo]);

  // Lazy-load attachment blobs once, on demand. The message list only carries
  // `has_file` metadata; the actual base64 is fetched here per message and
  // cached in `fileData`, so 6s polling never re-downloads image bytes.
  useEffect(() => {
    const need = messages.filter(m =>
      m.has_file && !m.is_deleted &&
      !fileData[m.id] && !fetchingFilesRef.current.has(m.id)
    );
    if (need.length === 0) return;
    need.forEach((m) => {
      fetchingFilesRef.current.add(m.id);   // attempt once; success caches, failure won't loop
      API.get(`/chat/message/${m.id}/file`)
        .then(r => {
          if (r.data?.file_data) {
            const url = `data:${r.data.file_type || m.file_type};base64,${r.data.file_data}`;
            setFileData(prev => ({ ...prev, [m.id]: url }));
            if (isNearBottom()) scrollToBottom(false);
          }
        })
        .catch(() => { fetchingFilesRef.current.delete(m.id); });  // allow a later retry
    });
  }, [messages, fileData, scrollToBottom]);

  // Re-scroll whenever an image inside the message list finishes loading IF
  // the user hasn't scrolled away from the bottom. Catches both initial-load
  // image decoding and newly-arriving message images.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onImgLoad = (ev) => {
      if (ev.target && ev.target.tagName === 'IMG') {
        if (isNearBottom()) scrollToBottom(false);
      }
    };
    el.addEventListener('load', onImgLoad, true); // capture phase to catch all <img>
    return () => el.removeEventListener('load', onImgLoad, true);
  }, [scrollToBottom]);

  // Smart polling: pause on hidden tab, immediate refetch on focus/visibility
  useEffect(() => {
    if (!roomId) return;
    const tick = () => { fetchNew(); };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (!document.hidden) {
        // Re-arm interval at fast cadence and fetch immediately
        clearInterval(pollRef.current);
        pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
        fetchNew();
      } else {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(tick, POLL_BACKOFF_HIDDEN_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchNew);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchNew);
    };
  }, [roomId, fetchNew]);

  // Load older messages when user scrolls near the top
  const loadOlder = useCallback(async () => {
    if (!roomId || loadingOlder || !hasMoreOlder || firstId === 0) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight || 0;
    try {
      const res = await API.get(`/chat/messages/${roomId}?before_id=${firstId}&limit=${OLDER_PAGE_SIZE}`);
      const data = Array.isArray(res.data) ? res.data : [];
      if (data.length === 0) {
        setHasMoreOlder(false);
      } else {
        setMessages(prev => mergeOlderMessages(prev, data));
        if (data.length < OLDER_PAGE_SIZE) setHasMoreOlder(false);
        // Preserve scroll position — anchor by adding the height delta
        requestAnimationFrame(() => {
          if (!el) return;
          const newHeight = el.scrollHeight;
          el.scrollTop = newHeight - prevHeight;
        });
      }
    } catch (_) {}
    setLoadingOlder(false);
  }, [roomId, loadingOlder, hasMoreOlder, firstId]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 80 && hasMoreOlder && !loadingOlder) {
      loadOlder();
    }
  }, [loadOlder, hasMoreOlder, loadingOlder]);

  const toBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const sendMessage = async ({ content = '', fileData: fData = null, fileType = null, fileName = null, localDataUrl = null } = {}) => {
    if (!roomId) return;
    if (!content.trim() && !fData) return;
    try {
      const payload = {
        room_id: roomId,
        content: content.trim(),
        file_data: fData,
        file_type: fileType,
        file_name: fileName,
      };
      if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
      const res = await API.post('/chat/send', payload);
      // Optimistically append (server response is metadata-only for files)
      if (res.data) {
        // Seed the file cache from what we just uploaded so the sender's own
        // attachment renders instantly without a round-trip re-fetch.
        if (localDataUrl && res.data.id) {
          fetchingFilesRef.current.add(res.data.id);
          setFileData(prev => ({ ...prev, [res.data.id]: localDataUrl }));
        }
        setMessages(prev => mergeNewMessages(prev, [res.data]));
        requestAnimationFrame(() => scrollToBottom(true));
        lastMarkedRef.current = Math.max(lastMarkedRef.current, res.data.id);
      }
      setReplyTo(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to send message';
      alert(detail);
    }
  };

  const handleSend = () => {
    sendMessage({ content: text });
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    let failures = 0;
    for (const file of files) {
      const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name);
      try {
        if (isImage) {
          const dataUrl = await compressImageToJpeg(file, 1920, 0.85);
          const base64 = dataUrl.split(',')[1];
          const fname = (file.name || 'photo').replace(/\.(heic|heif|png|gif|webp)$/i, '.jpg');
          await sendMessage({ fileData: base64, fileType: 'image/jpeg', fileName: fname, localDataUrl: dataUrl });
        } else {
          if (file.size > 15 * 1024 * 1024) { failures++; continue; }
          const base64 = await toBase64(file);
          await sendMessage({ fileData: base64, fileType: file.type, fileName: file.name, localDataUrl: `data:${file.type};base64,${base64}` });
        }
      } catch (err) {
        failures++;
      }
    }
    if (failures > 0) {
      alert(`${failures} file(s) could not be sent (max size 15MB per file).`);
    }
    e.target.value = '';
  };

  const handleVoice = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      alert('Voice recording is not supported on this browser.');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      alert('Voice recording is not supported on this browser. Try updating your iOS to version 14.3 or later.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = '';
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      for (const t of candidates) {
        if (MediaRecorder.isTypeSupported(t)) { mimeType = t; break; }
      }
      const options = mimeType ? { mimeType } : {};
      const mr = new MediaRecorder(stream, options);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (chunks.length === 0) { alert('No audio recorded. Please try again.'); return; }
        const rawType = mr.mimeType || mimeType || 'audio/webm';
        const finalType = rawType.split(';')[0];
        const blob = new Blob(chunks, { type: finalType });
        const ext = finalType.includes('mp4') ? 'mp4' : finalType.includes('ogg') ? 'ogg' : 'webm';
        try {
          const base64 = await toBase64(blob);
          await sendMessage({ fileData: base64, fileType: finalType, fileName: `voice-message.${ext}`, localDataUrl: `data:${finalType};base64,${base64}` });
        } catch (e) {
          alert('Failed to send voice message. Please try again.');
        }
      };
      mr.start(250);
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      alert('Microphone access denied. Please allow microphone access in your browser settings.');
    }
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Delete this message for everyone?')) return;
    try {
      await API.delete(`/chat/message/${msgId}`);
      setMessages(prev => prev.map(m =>
        m.id === msgId ? { ...m, is_deleted: true, has_file: false, content: null, file_data: null, file_type: null, file_name: null, reply_to: null } : m
      ));
      setFileData(prev => { const n = { ...prev }; delete n[msgId]; return n; });
    } catch (e) {
      alert('Failed to delete message.');
    }
  };

  const roleBadgeStyle = (role) => {
    const map = {
      admin: { background: 'rgba(220,38,38,0.1)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.25)' },
      tutor: { background: 'rgba(37,84,184,0.1)', color: '#2554b8', border: '1px solid rgba(37,84,184,0.25)' },
      student: { background: 'rgba(22,163,74,0.1)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.25)' },
    };
    return map[role] || { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
  };

  const renderReplyPreview = (parent, onLight = false) => {
    if (!parent) return null;
    const label = parent.is_deleted
      ? 'Deleted message'
      : (parent.content || (parent.file_type ? `[${parent.file_type.split('/')[0]}]` : '[message]'));
    return (
      <div style={{
        borderLeft: `3px solid ${onLight ? '#2554b8' : 'rgba(255,255,255,0.7)'}`,
        paddingLeft: 8,
        marginBottom: 6,
        opacity: 0.85,
        fontSize: 12,
        lineHeight: 1.4,
        background: onLight ? 'rgba(37,84,184,0.06)' : 'rgba(255,255,255,0.12)',
        borderRadius: 4,
        padding: '4px 8px',
      }}>
        <div style={{ fontWeight: 700, fontSize: 11 }}>{parent.sender_name}</div>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
      </div>
    );
  };

  const renderContent = (msg, isMe) => {
    if (msg.is_deleted) {
      return (
        <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
          🗑 This message was deleted
        </span>
      );
    }
    if (msg.has_file && msg.file_type) {
      const isImg = msg.file_type.startsWith('image/');
      const isAudio = msg.file_type.startsWith('audio/');
      const isVideo = msg.file_type.startsWith('video/');
      const isPdf = msg.file_type === 'application/pdf';
      const src = fileData[msg.id];
      // Blob still lazy-loading — show a lightweight placeholder instead of the
      // (now absent) inline base64.
      if (!src) {
        return (
          <div style={{ marginTop: msg.content ? 6 : 0, display: 'flex', alignItems: 'center', gap: 8, minWidth: 140, padding: '10px 4px', color: isMe ? 'rgba(255,255,255,0.85)' : '#94a3b8', fontSize: 12 }}>
            <span style={{ fontSize: 15 }}>{isImg ? '🖼' : isVideo ? '🎞' : isAudio ? '🎧' : '📄'}</span>
            <span>Loading {isImg ? 'image' : isPdf ? 'PDF' : isVideo ? 'video' : isAudio ? 'audio' : 'file'}…</span>
          </div>
        );
      }
      const showDownload = isImg || isVideo || isPdf;
      return (
        <div style={{ marginTop: msg.content ? 6 : 0, position: 'relative' }}>
          {isImg && (
            <img src={src} alt={msg.file_name || 'image'}
              onClick={() => setLightbox(src)}
              style={{ maxWidth: 220, maxHeight: 180, borderRadius: 8, display: 'block', cursor: 'zoom-in' }} />
          )}
          {isVideo && (
            <video controls src={src} style={{ maxWidth: 240, maxHeight: 200, borderRadius: 8, display: 'block' }} />
          )}
          {isPdf && (
            <button
              onClick={(e) => { e.stopPropagation(); openDataUrl(src); }}
              title="Tap to open — use the ⬇ button to save"
              style={{ color: isMe ? '#fff' : '#2554b8', fontSize: 13, fontWeight: 600, textDecoration: 'underline', display: 'block', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
              📄 {msg.file_name || 'Open PDF'}
            </button>
          )}
          {isAudio && (
            <audio controls src={src} style={{ display: 'block', maxWidth: 240 }} />
          )}
          {showDownload && (
            <button
              onClick={(e) => { e.stopPropagation(); downloadDataUrl(src, msg.file_name || (isPdf ? 'document.pdf' : isVideo ? 'video.mp4' : 'photo.jpg')); }}
              title="Save to device"
              style={{
                position: 'absolute', top: 6, right: 6,
                height: 26, padding: '0 10px', borderRadius: 13,
                background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex',
                alignItems: 'center', gap: 4,
              }}
            >⬇ Save</button>
          )}
        </div>
      );
    }
    return null;
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const utcTs = ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts) ? ts : ts + 'Z';
      const d = new Date(utcTs);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // ── WhatsApp-style date separators ──────────────────────────────────────────
  const _msgDate = (ts) => {
    if (!ts) return null;
    try {
      const utcTs = ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts) ? ts : ts + 'Z';
      const d = new Date(utcTs);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    } catch { return null; }
  };

  const formatDateLabel = (ts) => {
    const msgDay = _msgDate(ts);
    if (!msgDay) return '';
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.round((todayMid - msgDay) / dayMs);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 1 && diffDays < 7) {
      return msgDay.toLocaleDateString([], { weekday: 'long' });
    }
    // Older: include year only if not current year
    const opts = msgDay.getFullYear() === today.getFullYear()
      ? { day: 'numeric', month: 'long' }
      : { day: 'numeric', month: 'long', year: 'numeric' };
    return msgDay.toLocaleDateString([], opts);
  };

  const isSameDay = (a, b) => {
    const da = _msgDate(a);
    const db = _msgDate(b);
    if (!da || !db) return false;
    return da.getTime() === db.getTime();
  };

  const formatDateTime = (ts) => {
    if (!ts) return '';
    try {
      const utcTs = ts.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(ts) ? ts : ts + 'Z';
      const d = new Date(utcTs);
      return d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // Read receipts: count of OTHER users (not the sender) who have read
  const readsForMe = (msg) => {
    if (!msg.reads) return [];
    return msg.reads.filter(r => r.user_id !== msg.sender_id);
  };

  if (!roomId) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', color: '#94a3b8' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>💬</div>
        <p style={{ fontWeight: 700, fontSize: 16 }}>Select a chat room</p>
        <p style={{ fontSize: 13, marginTop: 4 }}>Choose a conversation from the left panel</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8faff', borderRadius: 16, overflow: 'hidden', border: '1px solid #e8edf8' }}>
      {/* Image lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out', padding:16 }}>
          <img src={lightbox} alt="full size"
            style={{ maxWidth:'100%', maxHeight:'100%', borderRadius:8, objectFit:'contain', boxShadow:'0 8px 40px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()} />
          <button onClick={() => downloadDataUrl(lightbox, 'photo.jpg')}
            style={{ position:'fixed', top:16, right:70, background:'rgba(255,255,255,0.18)', border:'none', color:'#fff', height:40, padding:'0 16px', borderRadius:20, fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}
            title="Save to device">⬇ Save</button>
          <button onClick={() => setLightbox(null)}
            style={{ position:'fixed', top:16, right:16, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', width:40, height:40, borderRadius:'50%', fontSize:22, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            ×
          </button>
        </div>
      )}

      {/* Seen-by modal */}
      {seenByModal && (
        <div onClick={() => setSeenByModal(null)}
          style={{ position:'fixed', inset:0, background:'rgba(15,31,69,0.55)', zIndex:9998, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:'18px 22px', minWidth:280, maxWidth:360, maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#0f1f45', marginBottom:14 }}>👀 Read by</div>
            {readsForMe(seenByModal).length === 0 ? (
              <div style={{ fontSize:12, color:'#94a3b8' }}>No one has read this yet.</div>
            ) : (
              readsForMe(seenByModal).map(r => (
                <div key={r.user_id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1e293b' }}>{r.user_name || 'User'}</div>
                    <div style={{ fontSize:10, fontWeight:600, color:'#64748b', textTransform:'capitalize' }}>{r.user_role === 'tutor' ? 'companion' : r.user_role}</div>
                  </div>
                  <div style={{ fontSize:11, color:'#64748b' }}>{formatDateTime(r.read_at)}</div>
                </div>
              ))
            )}
            <button onClick={() => setSeenByModal(null)} style={{ marginTop:12, width:'100%', padding:'8px 14px', borderRadius:8, border:'none', background:'#2554b8', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ padding: '14px 20px', background: '#fff', borderBottom: '1px solid #e8edf8', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#2554b8,#60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff', fontWeight: 800, flexShrink: 0 }}>
          {(studentName || 'C')[0]?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f1f45' }}>{studentName || 'Chat Room'}</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>Room #{roomId}</div>
        </div>
      </div>

      {/* Messages area */}
      <div ref={scrollRef} onScroll={onScroll} style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loadingOlder && (
          <div style={{ textAlign:'center', fontSize:11, color:'#94a3b8', padding:'6px 0' }}>Loading older messages…</div>
        )}
        {!hasMoreOlder && messages.length > 0 && (
          <div style={{ textAlign:'center', fontSize:10, color:'#cbd5e1', padding:'4px 0' }}>— Beginning of conversation —</div>
        )}
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>💬</div>
            <p style={{ fontSize: 13, fontWeight: 600 }}>No messages yet. Start the conversation!</p>
          </div>
        )}
        {messages.map((msg, idx) => {
          const isMe = msg.sender_id === myUserId || msg.sender_name === myName;
          const reads = readsForMe(msg);
          const readCount = reads.length;
          const prev = idx > 0 ? messages[idx - 1] : null;
          const showDateSep = !prev || !isSameDay(prev.created_at, msg.created_at);
          return (
            <React.Fragment key={msg.id || idx}>
              {showDateSep && (
                <div style={{ display:'flex', justifyContent:'center', margin:'12px 0 6px' }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#475569', background:'rgba(255,255,255,0.85)', padding:'4px 14px', borderRadius:20, border:'1px solid #e2e8f0', boxShadow:'0 1px 4px rgba(15,31,69,0.06)' }}>
                    {formatDateLabel(msg.created_at)}
                  </span>
                </div>
              )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', gap: 3 }}>
              {/* Sender info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                {!isMe && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
                    {msg.sender_role === 'admin' && !isAdmin ? 'Admin' : msg.sender_name}
                  </span>
                )}
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, ...roleBadgeStyle(msg.sender_role) }}>
                  {msg.sender_role === 'tutor' ? 'Companion' : msg.sender_role === 'admin' ? 'Admin' : msg.sender_role}
                </span>
                {isMe && <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>You</span>}
              </div>

              {/* Bubble + action buttons */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, width: '100%', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                {/* Reply button — hover-shows */}
                {!msg.is_deleted && (
                  <button
                    onClick={() => setReplyTo({
                      id: msg.id, sender_name: msg.sender_name,
                      content: msg.content, file_type: msg.file_type, file_name: msg.file_name,
                    })}
                    title="Reply"
                    style={{
                      width: 22, height: 22, borderRadius: 6, border: '1px solid #cbd5e1',
                      background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, opacity: 0.6,
                      order: isMe ? 0 : 2,
                    }}
                  >↩</button>
                )}
                <div style={{
                  maxWidth: '70%',
                  minWidth: 48,
                  padding: msg.is_deleted ? '8px 14px' : '10px 14px',
                  borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  background: msg.is_deleted ? '#f1f5f9' : (isMe ? 'linear-gradient(135deg,#2554b8,#3b82f6)' : '#fff'),
                  color: msg.is_deleted ? '#94a3b8' : (isMe ? '#fff' : '#1e293b'),
                  border: msg.is_deleted ? '1px dashed #cbd5e1' : (isMe ? 'none' : '1px solid #e2e8f0'),
                  boxShadow: msg.is_deleted ? 'none' : (isMe ? '0 4px 14px rgba(37,84,184,0.3)' : '0 2px 8px rgba(0,0,0,0.06)'),
                  fontSize: 13,
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                  order: 1,
                }}>
                  {!msg.is_deleted && renderReplyPreview(msg.reply_to, !isMe)}
                  {!msg.is_deleted && msg.content && <span>{msg.content}</span>}
                  {renderContent(msg, isMe)}
                </div>
                {/* Delete button — admins: any message; tutors: their own */}
                {(isAdmin || (myRole === 'tutor' && isMe)) && !msg.is_deleted && (
                  <button
                    onClick={() => handleDelete(msg.id)}
                    title="Delete message"
                    style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #fca5a5', background: 'rgba(220,38,38,0.06)', color: '#dc2626', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: 0.7, transition: 'opacity 0.2s', order: 3 }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                  >
                    🗑
                  </button>
                )}
              </div>

              {/* Timestamp + read receipts (sender-side only) */}
              <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:1 }}>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{formatTime(msg.created_at)}</div>
                {isMe && !msg.is_deleted && (
                  <button
                    onClick={() => setSeenByModal(msg)}
                    title={readCount > 0 ? `Seen by ${readCount}` : 'Not seen yet'}
                    style={{
                      background:'none', border:'none', padding:0, cursor:'pointer',
                      fontSize:11, fontWeight:700,
                      color: readCount > 0 ? '#2554b8' : '#cbd5e1',
                    }}
                  >
                    {readCount > 0 ? `✓✓ ${readCount}` : '✓'}
                  </button>
                )}
              </div>
            </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply preview pill above composer */}
      {replyTo && (
        <div style={{ padding:'8px 16px', background:'#eef3ff', borderTop:'1px solid #c7d8f8', display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ flex:1, fontSize:12, lineHeight:1.4, borderLeft:'3px solid #2554b8', paddingLeft:8, overflow:'hidden' }}>
            <div style={{ fontWeight:700, fontSize:11, color:'#2554b8' }}>Replying to {replyTo.sender_name}</div>
            <div style={{ color:'#475569', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {replyTo.content || (replyTo.file_type ? `[${replyTo.file_type.split('/')[0]}]` : '[message]')}
            </div>
          </div>
          <button onClick={() => setReplyTo(null)}
            style={{ width:26, height:26, borderRadius:6, border:'none', background:'#fff', color:'#64748b', cursor:'pointer', fontSize:16 }}
            title="Cancel reply">×</button>
        </div>
      )}

      {/* Bottom bar */}
      <div style={{ padding: '12px 16px', background: '#fff', borderTop: '1px solid #e8edf8', display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          onClick={() => fileRef.current?.click()}
          title="Attach files — pick multiple photos or a PDF (max 15MB each)"
          style={{ width: 38, height: 38, borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8faff', color: '#64748b', fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#2554b8'; e.currentTarget.style.color = '#2554b8'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
        >
          📎
        </button>
        <button
          onClick={handleVoice}
          title={recording ? 'Stop recording' : 'Record voice message'}
          style={{ width: 38, height: 38, borderRadius: 10, border: `1.5px solid ${recording ? '#dc2626' : '#e2e8f0'}`, background: recording ? 'rgba(220,38,38,0.08)' : '#f8faff', color: recording ? '#dc2626' : '#64748b', fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s', animation: recording ? 'pulse 1.5s infinite' : 'none' }}
        >
          {recording ? '⏹' : '🎙'}
        </button>
        {recording && (
          <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, flexShrink: 0 }}>Recording...</span>
        )}
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          /* 16px is the threshold below which iOS Safari auto-zooms on focus
             (and never zooms back) — keep it at 16 to stop the jarring zoom. */
          style={{ flex: 1, padding: '9px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontSize: 16, outline: 'none', fontFamily: 'inherit', transition: 'border-color 0.2s' }}
          onFocus={e => { e.currentTarget.style.borderColor = '#2554b8'; }}
          onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim()}
          style={{ height: 38, padding: '0 18px', borderRadius: 10, border: 'none', background: text.trim() ? 'linear-gradient(135deg,#2554b8,#3b82f6)' : '#e2e8f0', color: text.trim() ? '#fff' : '#94a3b8', fontWeight: 700, fontSize: 13, cursor: text.trim() ? 'pointer' : 'not-allowed', flexShrink: 0, transition: 'all 0.2s', boxShadow: text.trim() ? '0 4px 12px rgba(37,84,184,0.3)' : 'none' }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
